import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { LinkIcon } from "@heroicons/react/24/outline";
import { CONSENT_PATH } from "../../legal/operator";
import shopService, { ACCOUNT_LINK_OUTCOMES } from "../../service/shop.service";

const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SAFE_FAILURE_MESSAGE =
  "Не удалось отправить письмо, попробуйте позже. Если ошибка повторяется, обратитесь в поддержку.";

const SUCCESS_MESSAGE =
  "Письмо отправлено. Проверьте почту и папку Спам.";

const INVALID_EMAIL_MESSAGE = "Введите корректную почту.";

const RATE_LIMITED_MESSAGE =
  "Слишком много запросов. Повторите отправку позже. Если письмо уже пришло, используйте его.";

const UNCONFIRMED_MESSAGE =
  "Не удалось подтвердить отправку письма. Проверьте почту и папку Спам: если письма нет, повторите попытку позже.";

const SENDING_MESSAGE = "Отправляем письмо...";

const SHOP_LINK_CHANNELS = {
  eshop: "в Telegram-бот Nintendo eShop или в сообщество Nintendo eShop во ВКонтакте",
  psn: "в Telegram-бот PlayStation",
  steam: "в Telegram-бот Steam",
};

const ERROR_MESSAGES = {
  [ACCOUNT_LINK_OUTCOMES.DELIVERY_FAILED]: SAFE_FAILURE_MESSAGE,
  [ACCOUNT_LINK_OUTCOMES.INVALID_EMAIL]: INVALID_EMAIL_MESSAGE,
  [ACCOUNT_LINK_OUTCOMES.RATE_LIMITED]: RATE_LIMITED_MESSAGE,
  [ACCOUNT_LINK_OUTCOMES.UNAVAILABLE]: UNCONFIRMED_MESSAGE,
  [ACCOUNT_LINK_OUTCOMES.UNKNOWN]: UNCONFIRMED_MESSAGE,
};

function getSuccessMessage(shop) {
  const channels = SHOP_LINK_CHANNELS[shop] || "в Telegram-бот магазина";
  return `${SUCCESS_MESSAGE} Чтобы завершить привязку, отправьте команду из письма ${channels}. Без этого шага аккаунт не привязан.`;
}

const SHOP_DISPLAY_NAMES = {
  eshop: "Nintendo eShop",
  steam: "Steam",
  psn: "PlayStation",
};

function getShopDisplayName(shop) {
  return SHOP_DISPLAY_NAMES[shop] || "выбранного магазина";
}

function getEmailError(email) {
  if (!email) return "Введите почту";
  if (!EMAIL_PATTERN.test(email)) return INVALID_EMAIL_MESSAGE;
  return null;
}

export function shouldOpenAccountLinkModal(search = "") {
  return new URLSearchParams(search).has("linkAccount");
}

function AccountLinkBlock({ shop, disabled = false, className = "", openOnLoad = false, variant = "default" }) {
  const modalTitleId = useId();
  const tooltipId = useId();
  const [showTooltip, setShowTooltip] = useState(false);
  const isDiscountTrigger = variant === "discount";
  const triggerLabel = "Связать бота с почтой для общей скидки";
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [hasConsent, setHasConsent] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [hasSentEmail, setHasSentEmail] = useState(false);
  const requestInFlightRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const trimmedEmail = email.trim();
  const emailError = getEmailError(trimmedEmail);
  const canSubmit = !disabled && !isPending && !hasSentEmail && hasConsent && !emailError;
  const shopDisplayName = getShopDisplayName(shop);

  const closeModal = () => {
    if (isPending) return;
    setIsOpen(false);
    setStatusMessage("");
    setErrorMessage("");
    setHasSentEmail(false);
  };

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setShowTooltip(false);
    setEmail("");
    setHasConsent(false);
    setStatusMessage("");
    setErrorMessage("");
    setHasSentEmail(false);
    setIsOpen(true);
  }, [disabled]);

  useEffect(() => {
    if (openOnLoad) {
      handleOpen();
    }
  }, [openOnLoad, handleOpen]);

  const handleEmailChange = (event) => {
    const nextEmail = event.target.value;
    const nextEmailError = getEmailError(nextEmail.trim());

    setEmail(nextEmail);
    setStatusMessage("");
    setErrorMessage(nextEmail && nextEmailError ? nextEmailError : "");
    setHasSentEmail(false);
  };

  const handleRequest = async (event) => {
    event.preventDefault();
    if (requestInFlightRef.current || isPending || disabled || hasSentEmail) return;

    if (emailError) {
      setErrorMessage(emailError);
      return;
    }

    if (!hasConsent) {
      setErrorMessage("Подтвердите согласие на обработку персональных данных.");
      return;
    }

    requestInFlightRef.current = true;
    setErrorMessage("");
    setIsPending(true);
    setStatusMessage(SENDING_MESSAGE);

    let outcome = ACCOUNT_LINK_OUTCOMES.UNAVAILABLE;
    try {
      const result = await shopService.requestAccountLink({
        shopType: shop,
        email: trimmedEmail,
      });
      outcome = result?.outcome;
    } catch (_error) {
      outcome = ACCOUNT_LINK_OUTCOMES.UNAVAILABLE;
    } finally {
      requestInFlightRef.current = false;
    }

    if (!isMountedRef.current) return;

    setIsPending(false);
    if (outcome === ACCOUNT_LINK_OUTCOMES.EMAIL_SENT) {
      setStatusMessage(getSuccessMessage(shop));
      setErrorMessage("");
      setHasSentEmail(true);
      return;
    }

    setStatusMessage("");
    setErrorMessage(ERROR_MESSAGES[outcome] || UNCONFIRMED_MESSAGE);
  };

  const buttonClassName = className || (isDiscountTrigger
    ? "inline-flex h-8 min-w-[36px] shrink-0 items-center justify-center gap-0.5 rounded-full text-base font-semibold text-blue-700 transition hover:bg-blue-50 hover:text-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-55 dark:text-blue-300 dark:hover:bg-blue-950 dark:hover:text-blue-200"
    :
    "rounded-full border border-blue-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-blue-700 shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-55 dark:border-blue-800 dark:bg-gray-900/95 dark:text-blue-300 dark:hover:bg-blue-950");

  return (
    <>
      <span className="relative inline-flex">
      <button
        type="button"
        className={buttonClassName}
        onClick={handleOpen}
        disabled={disabled}
        aria-label={isDiscountTrigger ? triggerLabel : undefined}
        aria-describedby={isDiscountTrigger && showTooltip ? tooltipId : undefined}
        onMouseEnter={() => isDiscountTrigger && !disabled && !isOpen && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => isDiscountTrigger && !disabled && !isOpen && setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        onKeyDown={(event) => event.key === "Escape" && setShowTooltip(false)}
      >
        {isDiscountTrigger ? (
          <>
            <span aria-hidden="true">(</span>
            <LinkIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            <span aria-hidden="true">)</span>
          </>
        ) : "Привязать аккаунт"}
      </button>
      {isDiscountTrigger && showTooltip && (
        <span
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none absolute bottom-full right-0 z-40 mb-2 w-max max-w-[220px] rounded-md border border-blue-500 bg-[#f7f8fa] px-2 py-1.5 text-left text-xs font-normal leading-4 text-blue-700 shadow-sm dark:bg-[#222222] dark:text-blue-300"
        >
          {triggerLabel}
          <span aria-hidden="true" className="absolute -bottom-[5px] right-3 h-2 w-2 rotate-45 border-b border-r border-blue-500 bg-[#f7f8fa] dark:bg-[#222222]" />
        </span>
      )}
      </span>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
        >
          <form
            className="w-full max-w-lg rounded-2xl bg-white p-5 text-left shadow-2xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-700"
            onSubmit={handleRequest}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3
                  id={modalTitleId}
                  className="text-lg font-bold text-gray-950 dark:text-white"
                >
                  Привязать аккаунт
                </h3>
                <div className="mt-1 space-y-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
                  <p>
                    Магазин: <span className="font-semibold text-gray-900 dark:text-white">{shopDisplayName}</span>.
                  </p>
                  <p>На указанный email отправим одноразовый код для бота.</p>
                </div>
              </div>
              <button
                type="button"
                className="rounded-full px-2 py-1 text-xl leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                onClick={closeModal}
                disabled={isPending}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <input
              type="email"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-950 outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:ring-blue-950"
              placeholder="Ваш email"
              value={email}
              onChange={handleEmailChange}
              disabled={isPending}
              aria-label="Email"
            />

            <label className="mt-4 flex items-start gap-3 rounded-xl bg-gray-50 p-3 text-sm leading-5 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                checked={hasConsent}
                onChange={(event) => setHasConsent(event.target.checked)}
                disabled={isPending}
              />
              <span>
                Даю согласие на{" "}
                <a
                  className="font-semibold text-blue-700 hover:underline dark:text-blue-300"
                  href={CONSENT_PATH}
                  target="_blank"
                  rel="noreferrer"
                >
                  обработку персональных данных
                </a>
              </span>
            </label>

            {statusMessage && (
              <p
                className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm leading-5 text-green-700 dark:bg-green-950 dark:text-green-300"
                role="status"
                aria-live="polite"
              >
                {statusMessage}
              </p>
            )}

            {errorMessage && (
              <p
                className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm leading-5 text-red-600 dark:bg-red-950 dark:text-red-300"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className="min-h-[42px] flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800"
                onClick={closeModal}
                disabled={isPending}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="flex min-h-[42px] flex-1 items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-base font-semibold leading-normal text-white shadow-sm transition duration-200 hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-blue-600"
                disabled={!canSubmit}
              >
                {isPending ? "Отправляем..." : "Отправить код"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default AccountLinkBlock;
